import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Cell,
} from 'recharts';
import { fmtMoneyShort, fmtMoney } from '../engine.js';

const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';
const TOOLTIP_CLS = 'bg-[rgba(18,21,28,0.95)] border border-border-strong rounded-sm px-[0.7rem] py-2 min-w-[180px] text-[0.8rem] text-text';

export default function TornadoChart({ rows = [], height = 320, label = '12mo net FCF impact' }) {
  if (!rows.length) {
    return <div className={HINT}>No sensitivity drivers — check scenario.</div>;
  }

  const maxAbs = Math.max(...rows.map(r => r.absImpact), 1);
  const data = rows.map(r => ({
    label: r.label,
    hint: r.hint,
    plus:  Math.max(0, r.plusDelta),
    minus: Math.min(0, r.plusDelta),
    plusAlt:  Math.max(0, r.minusDelta),
    minusAlt: Math.min(0, r.minusDelta),
    plusDelta: r.plusDelta,
    minusDelta: r.minusDelta,
  }));

  const fmtX = (v) => fmtMoneyShort(v);

  const customTooltip = ({ active, payload, label: name }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div className={TOOLTIP_CLS}>
        <strong className="block mb-1 text-accent-gold">{name}</strong>
        <p className="text-[0.7rem] text-text-muted m-0 mb-[0.35rem]">{d.hint}</p>
        <div className="tabular-nums text-[0.8rem] text-accent-green">
          +20% → {d.plusDelta >= 0 ? '+' : ''}{fmtMoney(d.plusDelta, { decimals: 2 })}
        </div>
        <div className="tabular-nums text-[0.8rem] text-accent-red">
          −20% → {d.minusDelta >= 0 ? '+' : ''}{fmtMoney(d.minusDelta, { decimals: 2 })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-[0.4rem]">
      <div className="text-[0.78rem] text-text-muted tracking-[0.04em] uppercase">{label} — ±20% sensitivity</div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          stackOffset="sign"
          margin={{ top: 16, right: 24, bottom: 0, left: 30 }}
        >
          <CartesianGrid stroke="var(--grid)" strokeDasharray="3 3" />
          <XAxis
            type="number"
            domain={[-maxAbs * 1.1, maxAbs * 1.1]}
            tickFormatter={fmtX}
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            stroke="var(--border)"
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
            stroke="var(--border)"
            width={140}
          />
          <Tooltip content={customTooltip} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
          <ReferenceLine x={0} stroke="var(--border-strong)" />
          <Bar dataKey="plus"  stackId="up" fill="#4ade80" name="+20% (favorable)" isAnimationActive={false} />
          <Bar dataKey="minus" stackId="up" fill="#f87171" name="+20% (unfavorable)" isAnimationActive={false} />
          <Bar dataKey="plusAlt"  stackId="down" fill="#4ade80" name="−20% (favorable)" isAnimationActive={false} />
          <Bar dataKey="minusAlt" stackId="down" fill="#f87171" name="−20% (unfavorable)" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
